import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Filter } from 'lucide-react';
import techImage from '../assets/images/2.jpg';
import nuroImage from '../assets/images/1.jpg';
import oncologyImage from '../assets/images/oncology.jpg';
import rareDiseases from '../assets/images/rare.jpg';
import immunImage from '../assets/images/immun.jpg';
import geneImage from '../assets/images/gene.jpg';
import equpImage from '../assets/images/equp.jpg';
import cardioImage from '../assets/images/cardio.jpg';
import infuImage from '../assets/images/infu.jpg';
import dermImage from '../assets/images/derm.jpg';
import digestiveImage from '../assets/images/digestive.jpg';
import optoImage from '../assets/images/opto.jpg';
import lungImage from '../assets/images/lung.jpg';
import qualityImage from '../assets/images/quality.jpg';
import creativeImage from '../assets/images/creative.jpg';


const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['All', 'Clinical Trial Management', 'Regulatory Affairs', 'Biostatistics & Data Management', 'Medical Writing', 'Pharmacovigilance', 'Drug Recall Management'];
const case_studies = [
        // # Clinical Trial Management (4 case studies)
        {
            "id": "1",
            "title": "Accelerating Phase III Oncology Trial",
            "client": "Major Pharma Company",
            "industry": "Oncology",
            "challenge": "Complex multi-site Phase III trial with tight enrollment timelines and regulatory requirements across 15 countries.",
            "solution": "Implemented AI-powered patient recruitment, risk-based monitoring, and our Sclin EDC system for real-time data capture.",
            "results": ["40% faster enrollment", "30% reduction in protocol deviations", "On-time regulatory submissions"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/2jmfx93h_scientists-mix-red-and-orange-chemicals-in-a-glass-2025-01-16-13-51-09-utc.jpg",
            "category": "Clinical Trial Management"
        },
        {
            "id": "2",
            "title": "Global Vaccine Trial Success",
            "client": "International Pharma",
            "industry": "Vaccines",
            "challenge": "Urgent vaccine development program requiring rapid trial execution across multiple continents.",
            "solution": "Mobilized global clinical operations team, implemented adaptive trial design, and utilized DocuDefender eTMF.",
            "results": ["Trial completed 3 months ahead of schedule", "99.8% data quality score", "Successful regulatory approval"],
            "image_url": creativeImage,
            "category": "Clinical Trial Management"
        },
        {
            "id": "3",
            "title": "Pediatric Rare Disease Trial",
            "client": "Biotech Innovator",
            "industry": "Rare Disease",
            "challenge": "First-in-class treatment for pediatric rare disease with limited patient population and complex endpoints.",
            "solution": "Developed innovative recruitment strategy through patient advocacy groups, implemented adaptive trial design with interim analyses.",
            "results": ["Enrolled 150% of target patients", "FDA Breakthrough Therapy designation", "Accelerated approval pathway"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/phhm3ph8_analyzing-medical-data-on-monitors-in-modern-lab-s-2025-05-30-14-51-55-utc.jpg",
            "category": "Clinical Trial Management"
        },
        {
            "id": "4",
            "title": "Multi-Regional Cardiovascular Study",
            "client": "Global Pharmaceutical",
            "industry": "Cardiovascular",
            "challenge": "Large-scale cardiovascular outcomes trial requiring 20,000 patients across 35 countries with complex logistics.",
            "solution": "Deployed centralized monitoring hub, implemented remote patient monitoring technology, and established regional coordination centers.",
            "results": ["25% reduction in site activation time", "15% cost savings", "Zero major protocol amendments"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/dty5g4ab_business-evolution-with-ai-interaction-with-moder-2024-12-07-00-52-21-utc.jpg",
            "category": "Clinical Trial Management"
        },
        // # Regulatory Affairs (4 case studies)
        {
            "id": "5",
            "title": "FDA Approval for Novel Biologics",
            "client": "Biotech Startup",
            "industry": "Biologics",
            "challenge": "First-time sponsor navigating complex BLA submission process with limited regulatory experience.",
            "solution": "Provided end-to-end regulatory strategy, prepared complete submission dossier, and managed FDA communications.",
            "results": ["Successful BLA approval", "6-month review cycle", "Zero FDA deficiency letters"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/vvnjwp1b_abstract-polygonal-head-outline-on-dark-backdrop-w-2025-10-15-01-46-07-utc.jpg",
            "category": "Regulatory Affairs"
        },
        {
            "id": "6",
            "title": "Expedited IND Submission",
            "client": "Emerging Pharma",
            "industry": "Oncology",
            "challenge": "Urgent need to file IND for breakthrough cancer therapy within 60 days to maintain competitive advantage.",
            "solution": "Assembled dedicated regulatory team, implemented parallel processing workflows, and conducted pre-IND meeting preparation.",
            "results": ["IND filed in 45 days", "FDA cleared to proceed without clinical hold", "First patient dosed within 90 days"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/5wuz3rbr_neurology-research-doctor-examining-a-human-brain-2025-04-05-07-19-24-utc.jpg",
            "category": "Regulatory Affairs"
        },
        {
            "id": "7",
            "title": "Global Regulatory Harmonization",
            "client": "Multinational Pharma",
            "industry": "Immunology",
            "challenge": "Simultaneous regulatory submissions required in US, EU, Japan, and 12 other markets with varying requirements.",
            "solution": "Developed global regulatory strategy, created region-specific dossiers, and managed parallel submissions with local partners.",
            "results": ["Approvals in all 15 markets within 18 months", "Harmonized labeling across regions", "30% cost reduction vs separate filings"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/2jmfx93h_scientists-mix-red-and-orange-chemicals-in-a-glass-2025-01-16-13-51-09-utc.jpg",
            "category": "Regulatory Affairs"
        },
        {
            "id": "8",
            "title": "Medical Device 510(k) Clearance",
            "client": "MedTech Company",
            "industry": "Medical Devices",
            "challenge": "Novel diagnostic device requiring substantial equivalence demonstration with limited predicate options.",
            "solution": "Conducted comprehensive predicate analysis, prepared robust clinical data package, and managed FDA Q-Sub process.",
            "results": ["510(k) clearance in first submission cycle", "4-month review time", "Expanded indications approved"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/phhm3ph8_analyzing-medical-data-on-monitors-in-modern-lab-s-2025-05-30-14-51-55-utc.jpg",
            "category": "Regulatory Affairs"
        },
        // # Biostatistics & Data Management (3 case studies)
        {
            "id": "9",
            "title": "Complex Adaptive Trial Analysis",
            "client": "Pharma Research Division",
            "industry": "Neuroscience",
            "challenge": "Adaptive Phase II/III trial with multiple interim analyses and complex dose-finding component.",
            "solution": "Designed Bayesian adaptive statistical framework, implemented real-time data monitoring, and provided IDMC support.",
            "results": ["Optimal dose identified 6 months earlier", "35% reduction in total sample size", "Successful Phase III transition"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/vvnjwp1b_abstract-polygonal-head-outline-on-dark-backdrop-w-2025-10-15-01-46-07-utc.jpg",
            "category": "Biostatistics & Data Management"
        },
        {
            "id": "10",
            "title": "CDISC Implementation Project",
            "client": "Mid-Size Biotech",
            "industry": "Multiple",
            "challenge": "Company-wide transition to CDISC standards required for upcoming FDA submissions with tight deadlines.",
            "solution": "Developed comprehensive CDISC implementation plan, created standardized datasets (SDTM/ADaM), and trained internal teams.",
            "results": ["Full CDISC compliance achieved", "50% reduction in submission preparation time", "Reusable standards library created"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/phhm3ph8_analyzing-medical-data-on-monitors-in-modern-lab-s-2025-05-30-14-51-55-utc.jpg",
            "category": "Biostatistics & Data Management"
        },
        {
            "id": "11",
            "title": "Real-World Data Integration",
            "client": "Health System Partner",
            "industry": "Multiple",
            "challenge": "Integration of electronic health records with clinical trial data for hybrid study design.",
            "solution": "Built secure data integration platform, implemented standardized data mapping, and ensured HIPAA compliance.",
            "results": ["95% data capture rate from EHR", "Real-time safety signal detection", "Regulatory acceptance of RWD"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/dty5g4ab_business-evolution-with-ai-interaction-with-moder-2024-12-07-00-52-21-utc.jpg",
            "category": "Biostatistics & Data Management"
        },
        // # Medical Writing (3 case studies)
        {
            "id": "12",
            "title": "Accelerated NDA Submission Package",
            "client": "Specialty Pharma", 
            "industry": "Dermatology",
            "challenge": "Complete NDA submission package required within 6 months including CSRs for 5 pivotal trials.",
            "solution": "Deployed dedicated writing team, implemented parallel document development, and utilized automated QC tools.",
            "results": ["All documents completed on schedule", "First-cycle FDA approval", "Zero content-related queries"],
            "image_url": nuroImage,
            "category": "Medical Writing"
        },
        {
            "id": "13",
            "title": "Protocol Development Excellence",
            "client": "Academic Medical Center",
            "industry": "Oncology",
            "challenge": "Complex basket trial protocol with multiple cohorts and biomarker-driven treatment assignments.",
            "solution": "Developed modular protocol structure, created comprehensive statistical analysis plan, and prepared IRB submission package.",
            "results": ["Protocol approved by IRB in first submission", "Enrolled across all cohorts within 12 months", "Published in top-tier journal"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/dty5g4ab_business-evolution-with-ai-interaction-with-moder-2024-12-07-00-52-21-utc.jpg",
             "category": "Medical Writing"
        },
        {
            "id": "14",
            "title": "Global Regulatory Document Suite",
            "client": "International Biotech",
            "industry": "Cell & Gene Therapy",
            "challenge": "Complete regulatory document suite needed for simultaneous global submissions of advanced therapy.",
            "solution": "Created comprehensive document package including IB, IMPD, protocols, and regional-specific documents.",
            "results": ["Documents accepted by FDA, EMA, and PMDA", "Consistent messaging across regions", "6-week delivery ahead of schedule"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/vvnjwp1b_abstract-polygonal-head-outline-on-dark-backdrop-w-2025-10-15-01-46-07-utc.jpg",
            "category": "Medical Writing"
        },
        // # Pharmacovigilance (3 case studies)
        {
            "id": "15",
            "title": "Real-World Evidence Study",
            "client": "Medical Device Company",
            "industry": "Medical Devices",
            "challenge": "Large-scale post-market surveillance study requiring integration of multiple data sources.",
            "solution": "Designed comprehensive RWE study protocol, implemented advanced analytics, and provided safety monitoring.",
            "results": ["500,000+ patient records analyzed", "Critical safety insights identified", "Regulatory compliance maintained"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/phhm3ph8_analyzing-medical-data-on-monitors-in-modern-lab-s-2025-05-30-14-51-55-utc.jpg",
            "category": "Pharmacovigilance"
        },
        {
            "id": "16",
            "title": "Global Safety Database Migration",
            "client": "Large Pharma",
            "industry": "Multiple",
            "challenge": "Migration of 15+ years of safety data to new pharmacovigilance system while maintaining regulatory compliance.",
            "solution": "Developed comprehensive migration strategy, validated data integrity, and ensured continuous regulatory reporting.",
            "results": ["Zero data loss during migration", "100% regulatory compliance maintained", "30% improvement in case processing time"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/5wuz3rbr_neurology-research-doctor-examining-a-human-brain-2025-04-05-07-19-24-utc.jpg",
            "category": "Pharmacovigilance"
        },
        {
            "id": "17",
            "title": "Signal Detection Enhancement",
            "client": "Global Pharma",
            "industry": "Cardiovascular",
            "challenge": "Need for enhanced signal detection capabilities across large product portfolio with limited resources.",
            "solution": "Implemented AI-powered signal detection platform, established automated safety monitoring, and created predictive models.",
            "results": ["50% faster signal detection", "Reduced false positive rate by 40%", "Proactive risk management achieved"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/dty5g4ab_business-evolution-with-ai-interaction-with-moder-2024-12-07-00-52-21-utc.jpg",
            "category": "Pharmacovigilance"
        },
        // # Drug Recall Management (3 case studies)
        {
            "id": "18",
            "title": "Rapid Product Recall Response",
            "client": "Generic Drug Manufacturer",
            "industry": "Generics",
            "challenge": "Urgent Class I recall required due to contamination issue affecting multiple lot numbers across 50 states.",
            "solution": "Mobilized 24/7 recall response team, coordinated with FDA and distributors, and managed public communications.",
            "results": ["95% product recovery within 72 hours", "Zero patient harm reported", "FDA commendation for response"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/phhm3ph8_analyzing-medical-data-on-monitors-in-modern-lab-s-2025-05-30-14-51-55-utc.jpg",
            "category": "Drug Recall Management"
        },
        {
            "id": "19",
            "title": "Proactive Quality Issue Management",
            "client": "OTC Manufacturer",
            "industry": "Consumer Health",
            "challenge": "Potential quality issue identified requiring assessment and possible voluntary recall decision.",
            "solution": "Conducted rapid risk assessment, developed decision framework, and prepared contingency recall plan.",
            "results": ["Issue resolved without recall", "Enhanced QC procedures implemented", "Prevented $50M+ in potential losses"],
            "image_url": qualityImage,
            "category": "Drug Recall Management"
        },
        {
            "id": "20",
            "title": "International Recall Coordination",
            "client": "Multinational Pharma",
            "industry": "Specialty Pharma",
            "challenge": "Coordinated product recall required across 25 countries with varying regulatory requirements and timelines.",
            "solution": "Established global recall coordination center, managed multi-country regulatory communications, and tracked recovery metrics.",
            "results": ["Successful recall across all markets", "98% recovery rate globally", "Regulatory relationships strengthened"],
            "image_url": "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/phhm3ph8_analyzing-medical-data-on-monitors-in-modern-lab-s-2025-05-30-14-51-55-utc.jpg",
            "category": "Drug Recall Management"
        }
    ];
export const CaseStudies = ({ onDemoClick }) => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [filteredStudies, setFilteredStudies] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setCaseStudies(case_studies);
    setFilteredStudies(case_studies);
    setLoading(false);
    // const fetchCaseStudies = async () => {
    //   try {
    //     const response = await axios.get(`${API_URL}/case-studies`);
    //     setCaseStudies(response.data);
    //     setFilteredStudies(response.data);
    //   } catch (error) {
    //     console.error('Error fetching case studies:', error);
    //   } finally {
    //     setLoading(false);
    //   }
    // };
    // fetchCaseStudies();
  }, []);

  useEffect(() => {
    if (activeCategory === 'All') {
      setFilteredStudies(caseStudies);
    } else {
      setFilteredStudies(caseStudies.filter(study => study.category === activeCategory));
    }
  }, [activeCategory, caseStudies]);

  return (
    <div className="min-h-screen bg-[#090B17] pt-24" data-testid="case-studies-page">
      {/* Hero Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#8F262B]/5 to-transparent" />
        <div className="w-full px-6 sm:px-12 lg:px-20 relative">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-label">Success Stories</span>
              <h1 className="text-4xl sm:text-5xl font-outfit font-bold text-silver-shine mt-4 mb-6" data-testid="case-studies-title">
                Case <span className="text-gradient">Studies</span>
              </h1>
              <p className="text-[#C9C9CD] text-lg">
                Explore how we've helped pharmaceutical, biotech, and medical device companies 
                achieve their clinical development goals.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Filter */}
      <section className="section-surface border-y border-[#2E3446]">
        <div className="w-full px-6 sm:px-12 lg:px-20 py-6">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 hide-scrollbar">
            <Filter className="w-5 h-5 text-[#9F999F] flex-shrink-0" />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === category
                    ? 'btn-gradient'
                    : 'bg-[#1B1F2C] border border-[#2E3446] text-[#C9C9CD] hover:border-teal-500/50 hover:text-[#8F262B]'
                }`}
                data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          {loading ? (
            <div className="grid md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="case-study-card animate-pulse">
                  <div className="h-48 bg-[#2E3446]" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-[#1B1F2C] rounded w-1/4" />
                    <div className="h-6 bg-[#1B1F2C] rounded w-3/4" />
                    <div className="h-4 bg-[#1B1F2C] rounded" />
                    <div className="h-4 bg-[#1B1F2C] rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredStudies.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#9F999F]">No case studies found for this category.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredStudies.map((study, index) => (
                <motion.article
                  key={study.id}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.06 }}
                  className="bg-[#0F1724] rounded-2xl overflow-hidden shadow-sm border border-[#2E3446] flex flex-col md:flex-row"
                  data-testid={`case-study-${study.id}`}
                >
                  <div className="md:w-1/3 h-56 md:h-auto relative flex-shrink-0">
                    <img
                      src={study.image_url}
                      alt={study.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/40 to-transparent" />
                    <div className="absolute top-4 left-4 bg-[#0B1220]/80 text-xs px-3 py-1 rounded-full text-[#9F999F]">{study.category}</div>
                  </div>

                  <div className="p-6 md:w-2/3 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-3 text-sm text-[#9F999F] mb-2">
                        <span className="font-medium text-[#C9C9CD]">{study.client}</span>
                        <span className="text-[#475569]">•</span>
                        <span>{study.industry}</span>
                      </div>

                      <h3 className="text-lg font-semibold text-silver-shine mb-3">{study.title}</h3>

                      <p className="text-[#9F999F] text-sm mb-3"><strong className="text-[#C9C9CD]">Challenge:</strong> {study.challenge}</p>

                      <p className="text-[#9F999F] text-sm mb-4"><strong className="text-[#C9C9CD]">Solution:</strong> {study.solution}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {study.results.map((result, idx) => (
                          <span key={idx} className="inline-flex items-center gap-2 bg-[#071018] border border-[#21303a] text-[#8F262B] px-3 py-1 rounded-full text-sm">
                            <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                            {result}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4 md:mt-0 flex items-center justify-between gap-4">
                      <a
                        href="#"
                        className="text-sm text-gradient font-semibold inline-flex items-center gap-2"
                        aria-label={`View details for ${study.title}`}
                      >
                        View Case Study <ArrowRight className="w-4 h-4" />
                      </a>

                      <button
                        className="btn-outline px-4 py-2 text-sm"
                        onClick={() => onDemoClick()}
                        aria-label="Request consultation"
                      >
                        Request Consultation
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-gradient rounded-2xl p-12 text-center"
          >
            <h2 className="section-title mb-4">
              Ready to Write Your <span className="text-gradient">Success Story?</span>
            </h2>
            <p className="text-[#C9C9CD] max-w-xl mx-auto mb-8">
              Let's discuss how VetForce Solutions can help achieve your clinical development goals
            </p>
            <button 
              onClick={onDemoClick}
              className="btn-gradient px-8 py-4 inline-flex items-center gap-2"
              data-testid="case-studies-cta-btn"
            >
              Start Your Journey
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
